// src/contexts/ProductsContext.js
import { createContext, useContext, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const ProductsContext = createContext();

const foodCategories = [
  'Boissons', 'Jus', 'Bissap', 'Glaçons', 'Pain', 'Gâteaux',
  'Crêpes', 'Placali', 'Dêguê', 'Poissons frais', "Plaquettes d'œufs", 'Atiéké',
];

const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

const interleaveByCategory = (list) => {
  const map = {};
  list.forEach((p) => {
    if (!map[p.category]) map[p.category] = [];
    map[p.category].push(p);
  });
  const result = [];
  let added;
  do {
    added = false;
    for (const cat in map) {
      if (map[cat].length) {
        result.push(map[cat].shift());
        added = true;
      }
    }
  } while (added);
  return result;
};

export function ProductsProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  /**
   * Charger tous les produits actifs avec les infos vendeur
   */
  const fetchProducts = useCallback(async (filters = {}) => {
    setLoadingProducts(true);
    setError(null);

    try {
      // products.id est INTEGER, user_id est UUID -> FK products_user_id_fkey
      const { data, error: fetchError } = await supabase
        .from('products')
        .select(`
          *,
          users!products_user_id_fkey (
            full_name, phone, shop_name, site, batiment, palier, chambre, is_verified
          )
        `)
        .eq('status', 'active');

      if (fetchError) throw fetchError;

      // Normaliser (aplatir la relation users) + mapper views_count
      let normalized = (data || []).map((p) => ({
        ...p,
        views: p.views_count || 0,
        full_name: p.users?.full_name,
        phone: p.users?.phone,
        shop_name: p.users?.shop_name,
        site: p.users?.site,
        batiment: p.users?.batiment,
        palier: p.users?.palier,
        chambre: p.users?.chambre,
        is_verified: p.users?.is_verified || false,
      }));

      // Filtrage catégorie
      if (filters.category && filters.category !== 'Tous') {
        normalized = normalized.filter((p) => p.category === filters.category);
      }

      // Filtrage recherche
      if (filters.searchQuery && filters.searchQuery.trim() !== '') {
        const q = filters.searchQuery.trim().toLowerCase();
        normalized = normalized.filter(
          (p) =>
            p.name?.toLowerCase().includes(q) ||
            p.description?.toLowerCase().includes(q) ||
            p.full_name?.toLowerCase().includes(q) ||
            p.shop_name?.toLowerCase().includes(q)
        );
      }

      // Trier : vérifiés en haut, puis nourriture, puis autres (entrelacés par catégorie)
      const verifiedProducts = shuffle(normalized.filter((p) => p.is_verified));
      const unverified = normalized.filter((p) => !p.is_verified);
      const foodProducts = shuffle(unverified.filter((p) => foodCategories.includes(p.category)));
      const otherProducts = shuffle(unverified.filter((p) => !foodCategories.includes(p.category)));
      const sorted = [
        ...interleaveByCategory(verifiedProducts),
        ...interleaveByCategory(foodProducts),
        ...interleaveByCategory(otherProducts),
      ];

      setProducts(sorted);
      return sorted;
    } catch (err) {
      console.error('[Products] Erreur chargement:', err);
      setError('Erreur lors du chargement des produits.');
      setProducts([]);
      throw err;
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  /**
   * Charger les détails d'un produit par ID (INTEGER)
   */
  const fetchProductDetails = useCallback(async (productId) => {
    try {
      const { data, error: fetchError } = await supabase
        .from('products')
        .select(`
          *,
          users!products_user_id_fkey (
            full_name, phone, shop_name, site, batiment, palier, chambre
          )
        `)
        .eq('id', productId)
        .eq('status', 'active')
        .single();

      if (fetchError) throw fetchError;

      const product = {
        ...data,
        full_name: data.users?.full_name,
        phone: data.users?.phone,
        shop_name: data.users?.shop_name,
        site: data.users?.site,
        batiment: data.users?.batiment,
        palier: data.users?.palier,
        chambre: data.users?.chambre,
      };

      setSelectedProduct(product);
      return product;
    } catch (err) {
      console.error('[Products] Erreur détails:', err);
      throw err;
    }
  }, []);

  /**
   * Charger les produits de l'utilisateur connecté
   */
  const fetchMyProducts = useCallback(async () => {
    try {
      const storedUser = localStorage.getItem('crou_user');
      if (!storedUser) throw new Error('Non authentifié');
      const user = JSON.parse(storedUser);

      const { data, error: fetchError } = await supabase
        .from('products')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      return data || [];
    } catch (err) {
      console.error('[Products] Erreur mes produits:', err);
      throw err;
    }
  }, []);

  /**
   * Créer un produit
   */
  const createProduct = useCallback(async (productData) => {
    try {
      const storedUser = localStorage.getItem('crou_user');
      if (!storedUser) throw new Error('Non authentifié');
      const user = JSON.parse(storedUser);

      const { data, error: insertError } = await supabase
        .from('products')
        .insert({
          user_id: user.id,
          name: productData.name,
          price: productData.price,
          category: productData.category,
          description: productData.description || '',
          condition: productData.condition || 'bon',
          images: productData.images || [],
          tags: productData.tags || [],
          status: 'active',
        })
        .select()
        .single();

      if (insertError) throw insertError;
      return data;
    } catch (err) {
      console.error('[Products] Erreur création:', err);
      throw err;
    }
  }, []);

  /**
   * Supprimer un produit
   */
  const deleteProduct = useCallback(async (productId) => {
    try {
      const { error: deleteError } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (deleteError) throw deleteError;
      setProducts((prev) => prev.filter((p) => p.id !== productId));
    } catch (err) {
      console.error('[Products] Erreur suppression:', err);
      throw err;
    }
  }, []);

  /**
   * Enregistrer une vue sur un produit
   */
  const recordView = useCallback(async (productId, userInfo = null) => {
    try {
      const userId = userInfo?.id || null;

      // Insert a new view row (each visit is counted, not just unique)
      const { error: insertError } = await supabase
        .from('views')
        .insert({ product_id: productId, user_id: userId });

      if (insertError) {
        console.error('[View] Erreur insert:', insertError);
        return;
      }

      // Mise à jour locale du compteur (optimistic update)
      setProducts((prev) =>
        prev.map((p) =>
          p.id === productId ? { ...p, views: (Number(p.views) || 0) + 1 } : p
        )
      );

      if (selectedProduct && selectedProduct.id === productId) {
        setSelectedProduct((prev) => ({
          ...prev,
          views: (Number(prev.views) || 0) + 1,
        }));
      }
    } catch (err) {
      console.error('[View] Erreur:', err);
    }
  }, [selectedProduct]);

  const resetProducts = useCallback(() => {
    setProducts([]);
    setSelectedProduct(null);
    setError(null);
    setLoadingProducts(false);
  }, []);

  const value = {
    products,
    loadingProducts,
    error,
    selectedProduct,
    setSelectedProduct,
    fetchProducts,
    fetchProductDetails,
    fetchMyProducts,
    createProduct,
    deleteProduct,
    recordView,
    resetProducts,
  };

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
}

export function useProducts() {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error('useProducts doit être utilisé dans un ProductsProvider');
  }
  return context;
}